import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { BatchDetailLink, ShipmentDetailLink } from "@/components/hub/HubEntityDetailLink";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { EM_DASH } from "@/lib/utils";
import {
  fetchSuntronicUnbilledLabor,
  type SuntronicUnbilledByDayRow,
  type SuntronicUnbilledFinishedGoodRow,
  type SuntronicUnbilledMatchedShipmentRow,
} from "./suntronicUnbilledLaborApi";

const PAGE_HELP = [
  "Accounts Payable Suntronic Labor page from Appsmith.",
  "",
  "The total is remaining finished-good service cost that is not fully matched to a manufacturer invoice. It is not a QuickBooks bill total.",
  "Outstanding invoices groups that remaining cost by the day the finished good was created.",
  "The finished-good table lists service-cost rows with no manufacturer invoice match, with batch and work-order fields.",
  "Matched batches and invoices lists shipped serialized lines at locations other than SGS that already have a manufacturer invoice.",
].join("\n");

function formatMoney(value: string | null): string {
  if (value == null || value.trim() === "") return EM_DASH;
  if (value.includes("$")) return value;
  const amount = Number.parseFloat(value);
  if (!Number.isFinite(amount)) return value;
  return amount.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function formatNumber(value: string | null): string {
  if (value == null || value.trim() === "") return EM_DASH;
  const amount = Number.parseFloat(value);
  if (!Number.isFinite(amount)) return value;
  return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatPercent(value: string | null): string {
  if (value == null || value.trim() === "") return EM_DASH;
  const amount = Number.parseFloat(value);
  if (!Number.isFinite(amount)) return value;
  return `${amount.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

function formatDate(value: string | null): string {
  if (value == null || value.trim() === "") return EM_DASH;
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function numeric(value: string | null): number {
  const amount = Number.parseFloat(value ?? "");
  return Number.isFinite(amount) ? amount : 0;
}

type ByDaySortKey =
  | "creation_day"
  | "total_service_cost_usd"
  | "line_items"
  | "units"
  | "average_remaining_percentage";

const BY_DAY_COLUMNS: HubDataTableColumn<SuntronicUnbilledByDayRow, ByDaySortKey>[] = [
  {
    id: "creation_day",
    header: "Creation day",
    sortKey: "creation_day",
    overflow: "nowrap",
    sortValue: (row) => row.creation_day ?? "",
    cell: (row) => formatDate(row.creation_day),
  },
  {
    id: "total_service_cost_usd",
    header: "Service cost",
    sortKey: "total_service_cost_usd",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.total_service_cost_usd),
    cell: (row) => formatMoney(row.total_service_cost_usd),
  },
  {
    id: "line_items",
    header: "Line items",
    sortKey: "line_items",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.line_items),
    cell: (row) => formatNumber(row.line_items),
  },
  {
    id: "units",
    header: "Units",
    sortKey: "units",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.units),
    cell: (row) => formatNumber(row.units),
  },
  {
    id: "average_remaining_percentage",
    header: "Average remaining",
    sortKey: "average_remaining_percentage",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.average_remaining_percentage),
    cell: (row) => formatPercent(row.average_remaining_percentage),
  },
];

type FinishedGoodSortKey =
  | "wofg_number"
  | "batch_id"
  | "mv_build_order_number"
  | "creation_day"
  | "sku"
  | "quantity"
  | "unit_cost"
  | "service_cost"
  | "mfr_ship_by_date";

const FINISHED_GOOD_COLUMNS: HubDataTableColumn<SuntronicUnbilledFinishedGoodRow, FinishedGoodSortKey>[] = [
  {
    id: "wofg_number",
    header: "WOFG",
    sortKey: "wofg_number",
    overflow: "nowrap",
    sortValue: (row) => row.wofg_number ?? "",
    cell: (row) => row.wofg_number ?? EM_DASH,
  },
  {
    id: "batch_id",
    header: "Batch",
    sortKey: "batch_id",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.batch_id),
    cell: (row) => {
      const batchId = Number(row.batch_id);
      return Number.isFinite(batchId) && batchId > 0 ? <BatchDetailLink batchId={batchId} /> : EM_DASH;
    },
  },
  {
    id: "mv_build_order_number",
    header: "Build order",
    sortKey: "mv_build_order_number",
    overflow: "nowrap",
    sortValue: (row) => row.mv_build_order_number ?? "",
    cell: (row) => row.mv_build_order_number ?? EM_DASH,
  },
  {
    id: "creation_day",
    header: "Created",
    sortKey: "creation_day",
    overflow: "nowrap",
    sortValue: (row) => row.creation_day ?? "",
    cell: (row) => formatDate(row.creation_day),
  },
  {
    id: "sku",
    header: "SKU",
    sortKey: "sku",
    overflow: "nowrap",
    sortValue: (row) => row.sku ?? "",
    cell: (row) => row.sku ?? EM_DASH,
  },
  {
    id: "quantity",
    header: "Quantity",
    sortKey: "quantity",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.quantity),
    cell: (row) => formatNumber(row.quantity),
  },
  {
    id: "unit_cost",
    header: "Unit cost",
    sortKey: "unit_cost",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.unit_cost),
    cell: (row) => formatMoney(row.unit_cost),
  },
  {
    id: "service_cost",
    header: "Service cost",
    sortKey: "service_cost",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.service_cost),
    cell: (row) => formatMoney(row.service_cost),
  },
  {
    id: "mfr_ship_by_date",
    header: "Ship by",
    sortKey: "mfr_ship_by_date",
    overflow: "nowrap",
    sortValue: (row) => row.mfr_ship_by_date ?? "",
    cell: (row) => formatDate(row.mfr_ship_by_date),
  },
];

type MatchedShipmentSortKey =
  | "mfr_invoice_numbers"
  | "date_shipped"
  | "shipment_id"
  | "batch_id"
  | "sku"
  | "location_name"
  | "shipment_quantity"
  | "total_matched_quantity"
  | "tla_quantity"
  | "tla_kit_quantity"
  | "tla_purchase_order_number";

const MATCHED_SHIPMENT_COLUMNS: HubDataTableColumn<SuntronicUnbilledMatchedShipmentRow, MatchedShipmentSortKey>[] = [
  {
    id: "mfr_invoice_numbers",
    header: "Invoices",
    sortKey: "mfr_invoice_numbers",
    sortValue: (row) => row.mfr_invoice_numbers ?? "",
    cell: (row) => row.mfr_invoice_numbers ?? EM_DASH,
  },
  {
    id: "date_shipped",
    header: "Shipped",
    sortKey: "date_shipped",
    overflow: "nowrap",
    sortValue: (row) => row.date_shipped ?? "",
    cell: (row) => formatDate(row.date_shipped),
  },
  {
    id: "shipment_id",
    header: "Shipment",
    sortKey: "shipment_id",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.shipment_id),
    cell: (row) => {
      const shipmentId = Number(row.shipment_id);
      return Number.isFinite(shipmentId) && shipmentId > 0 ? (
        <ShipmentDetailLink shipmentId={shipmentId} />
      ) : (
        EM_DASH
      );
    },
  },
  {
    id: "batch_id",
    header: "Batch",
    sortKey: "batch_id",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.batch_id),
    cell: (row) => {
      const batchId = Number(row.batch_id);
      return Number.isFinite(batchId) && batchId > 0 ? <BatchDetailLink batchId={batchId} /> : EM_DASH;
    },
  },
  {
    id: "sku",
    header: "SKU",
    sortKey: "sku",
    overflow: "nowrap",
    sortValue: (row) => row.sku ?? "",
    cell: (row) => row.sku ?? EM_DASH,
  },
  {
    id: "location_name",
    header: "Location",
    sortKey: "location_name",
    sortValue: (row) => row.location_name ?? "",
    cell: (row) => row.location_name ?? EM_DASH,
  },
  {
    id: "shipment_quantity",
    header: "Shipped qty",
    sortKey: "shipment_quantity",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.shipment_quantity),
    cell: (row) => formatNumber(row.shipment_quantity),
  },
  {
    id: "total_matched_quantity",
    header: "Matched qty",
    sortKey: "total_matched_quantity",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.total_matched_quantity),
    cell: (row) => formatNumber(row.total_matched_quantity),
  },
  {
    id: "tla_quantity",
    header: "TLA qty",
    sortKey: "tla_quantity",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.tla_quantity),
    cell: (row) => formatNumber(row.tla_quantity),
  },
  {
    id: "tla_kit_quantity",
    header: "Kit qty",
    sortKey: "tla_kit_quantity",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => numeric(row.tla_kit_quantity),
    cell: (row) => formatNumber(row.tla_kit_quantity),
  },
  {
    id: "tla_purchase_order_number",
    header: "Purchase order",
    sortKey: "tla_purchase_order_number",
    overflow: "nowrap",
    sortValue: (row) => row.tla_purchase_order_number ?? "",
    cell: (row) => row.tla_purchase_order_number ?? EM_DASH,
  },
];

function rowCountLabel(count: number): string {
  return `${count.toLocaleString()} rows`;
}

export function SuntronicUnbilledLaborPage(): JSX.Element {
  const reportQuery = useQuery({
    queryKey: ["accounting", "suntronic-unbilled-labor"],
    queryFn: fetchSuntronicUnbilledLabor,
    staleTime: 60_000,
  });

  const report = reportQuery.data;

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={PAGE_HELP}>Suntronic labor</PageTitleWithHelp>
      {reportQuery.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading Suntronic labor.
        </p>
      ) : null}
      {reportQuery.isError ? (
        <p className="text-sm text-red-700">
          {reportQuery.error instanceof Error ? reportQuery.error.message : "Could not load Suntronic labor."}
        </p>
      ) : null}
      {report ? (
        <>
          <p className="text-sm font-medium text-slate-900">
            Total Suntronic unbilled (not including QBO): {report.totalUnbilledUsd ?? EM_DASH}
          </p>
          <OutstandingByDayTable rows={report.byCreationDay} />
          <UnmatchedFinishedGoodsTable rows={report.unmatchedFinishedGoods} />
          <MatchedShipmentsTable rows={report.matchedShipments} />
        </>
      ) : null}
    </div>
  );
}

function OutstandingByDayTable({ rows }: { rows: SuntronicUnbilledByDayRow[] }): JSX.Element {
  const [sortKey, setSortKey] = useState<ByDaySortKey>("creation_day");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  function onSort(key: ByDaySortKey): void {
    if (key === sortKey) setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDirection("asc");
    }
  }

  return (
    <HubDataTable
      title="Outstanding invoices"
      headerExtra={<span className="text-xs font-normal text-slate-500">{rowCountLabel(rows.length)}</span>}
      columns={BY_DAY_COLUMNS}
      rows={rows}
      rowKey={(row) => row.creation_day ?? "none"}
      sortKey={sortKey}
      sortDir={sortDirection}
      onSort={onSort}
      empty="No outstanding finished-good service cost."
      tableClassName="w-max min-w-full"
    />
  );
}

function UnmatchedFinishedGoodsTable({ rows }: { rows: SuntronicUnbilledFinishedGoodRow[] }): JSX.Element {
  const [sortKey, setSortKey] = useState<FinishedGoodSortKey>("wofg_number");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  function onSort(key: FinishedGoodSortKey): void {
    if (key === sortKey) setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDirection("asc");
    }
  }

  return (
    <HubDataTable
      title="Finished goods with no manufacturer invoice"
      headerExtra={<span className="text-xs font-normal text-slate-500">{rowCountLabel(rows.length)}</span>}
      columns={FINISHED_GOOD_COLUMNS}
      rows={rows}
      rowKey={(row) => `${row.wofg_number ?? "wofg"}-${row.batch_id ?? "batch"}`}
      sortKey={sortKey}
      sortDir={sortDirection}
      onSort={onSort}
      empty="No unmatched finished goods with service cost."
      tableClassName="w-max min-w-full"
    />
  );
}

function MatchedShipmentsTable({ rows }: { rows: SuntronicUnbilledMatchedShipmentRow[] }): JSX.Element {
  const [sortKey, setSortKey] = useState<MatchedShipmentSortKey>("date_shipped");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  function onSort(key: MatchedShipmentSortKey): void {
    if (key === sortKey) setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDirection("asc");
    }
  }

  return (
    <HubDataTable
      title="Matched batches and invoices"
      headerExtra={<span className="text-xs font-normal text-slate-500">{rowCountLabel(rows.length)}</span>}
      columns={MATCHED_SHIPMENT_COLUMNS}
      rows={rows}
      rowKey={(row) => `${row.shipment_id ?? "s"}-${row.batch_item_id ?? "b"}`}
      sortKey={sortKey}
      sortDir={sortDirection}
      onSort={onSort}
      empty="No matched shipments."
      tableClassName="w-max min-w-full"
    />
  );
}
